import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {UserComposition} from 'containers/EditorPage';
import useLearningPathNavigation from 'features/useLearningPathNavigation';
import {Button, useModals} from 'letrus-ui';
import {useEffect, useState} from 'react';
import type {ReactNode} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import checkNetworkStatus from 'utils/functions/checkNetworkStatus';
import useIndexedDB from 'utils/hooks/useIndexedDB';
import {Activity} from 'APIs/letrusAPI/activities/types';
import {cachedActivity} from 'APIs/letrusAPI/activities/activities.caches';
import {useStartFocusModeLearningPath} from 'APIs/letrusAPI/learningPath/focusMode';
import ActivityStepper from '../ActivityStepper';
import DateTimeSpan from '../DateTimeSpan';
import DownloadButton from '../DownloadButton';
import styles from './DashboardCard.module.scss';
import FeedbackModal from '../FeedbackModal';
import FocusModeInstructionModal from './components/FocusModeInstructionModal';
import getFocusModeCardState from './focusModeUtils';

enum learningPathTypes {
  letrus = 'Letrus',
  school = 'Escola',
  knowledge = 'Conhecimentos',
  competence = 'Trilha de Competências',
  'mediated reading' = 'Leitura Mediada',
  diagnostics = 'Diagnóstica',
  special = 'Especial',
  'sensitization and interpretation' = 'Sensibilização',
  'self-evaluation' = 'Autoavaliação',
  ede = 'EDE',
  evaluative = 'Simulado',
  other = '',
}

export interface DashboardCardProps {
  activity: Activity;
  children?: JSX.Element;
  closeTour?: () => void;
  tags?: ReactNode;
}

type ActiveModal = 'feedback' | null;

function DashboardCard({
  activity,
  children,
  closeTour,
  tags,
}: DashboardCardProps): JSX.Element {
  const [dateTimeSubtitle, setDateTimeSubtitle] = useState('');
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [focusModeStartError, setFocusModeStartError] = useState('');
  const {navigateToLearningPath} = useLearningPathNavigation(activity);
  const [hasFinishedLocalComposition, setHasFinishedLocalComposition] =
    useState<boolean | undefined>(false);
  const compositionsIDB = useIndexedDB<UserComposition>('LocalCompositions');

  const navigate = useNavigate();
  const location = useLocation();

  const isTest = activity?.activity_type === 'test';
  const isLearningPath = activity?.activity_type === 'learning_path';

  const ButtonTexts = {
    in_progress:
      (isLearningPath && activity?.started) ||
      (isTest && activity.status === 'in_progress')
        ? 'Continuar atividade'
        : 'Iniciar atividade',
    opened: 'Iniciar atividade',
    scheduled: 'Em breve',
    finished: 'Ver atividade',
    reviewed: 'Ver atividade',
    expired: 'Expirada',
    awaiting: 'Em breve',
  };

  const isUnavailableActivity =
    activity?.status === 'scheduled' || activity?.status === 'awaiting';

  const activityType =
    activity?.learning_path_type ?? activity?.test_type ?? '';

  const activityName = activity?.name;

  const activitySubTitle =
    activity?.status === 'scheduled'
      ? dateTimeSubtitle
      : activity?.theme_name ?? '';

  const isFinishedActivity =
    activity.status === 'finished' || activity.status === 'expired';

  const isFuvestReviewGrid = Number(activity.review_grid_id) === 2;
  const isMultiGenreReviewGrid = Number(activity.review_grid_id) === 61;

  const activityStartDate = activity.start_datetime;

  const isConcludedActivity =
    isFinishedActivity ||
    activity.status === 'reviewed' ||
    activity.status === 'expired';

  const wasSeenByStudent = activity?.seen_by_student;

  const baseReviewedRoute = activity.uses_ai_comp
    ? '/devolutiva'
    : '/nova_devolutiva';

  const isEvaluativeActivity = activity?.learning_path_type === 'evaluative';
  const focusModeModalId = `focusModeInstruction-${activity.id}`;
  const modals = useModals([focusModeModalId]);
  const startFocusModeLearningPath = useStartFocusModeLearningPath();

  // PROF-2145 (AC #1 "contrato de ciência"): o modal de instruções reaparece na
  // reabertura porque o backend zera `started` e volta o status para
  // 'opened'/'in_progress'. A condição abaixo já cobre isso.
  const shouldShowFocusModeInstructionModal =
    isLearningPath &&
    activity?.uses_focus_mode &&
    !activity?.started &&
    (activity?.status === 'opened' || activity?.status === 'in_progress');

  useEffect(() => {
    const currentDate = new Date();
    const startDate = new Date(activityStartDate);

    const remainingTime = startDate.getTime() - currentDate.getTime();
    const DAY_MILLISECONDS = 1000 * 60 * 60 * 24;
    const HOUR_MILLISECONDS = 1000 * 60 * 60;

    if (remainingTime > DAY_MILLISECONDS) {
      const days = Math.floor(remainingTime / DAY_MILLISECONDS);
      setDateTimeSubtitle(`Faltam ${days} dias para o início da atividade.`);
    } else {
      const hours = Math.floor(remainingTime / HOUR_MILLISECONDS);
      setDateTimeSubtitle(`Faltam ${hours} horas para o início da atividade.`);
    }

    compositionsIDB?.getAll()?.then((localCompositions) => {
      const finishedLocalComposition = localCompositions?.filter(
        (finishedComposition) =>
          finishedComposition.id === String(activity?.composition_id) &&
          finishedComposition.finished,
      );

      setHasFinishedLocalComposition(finishedLocalComposition?.[0]?.finished);
    });
  }, []);

  function redirectToCreatedTest() {
    const compositionId = activity?.composition_id || activity.id;

    //  Preventive validation — avoids invalid routes such as {compositionId}undefined
    if (!compositionId) {
      return;
    }

    if (!baseReviewedRoute) {
      return;
    }

    if (
      activity.uses_write_preparation &&
      activity.composition_status === 'preparing'
    ) {
      navigate(`/atividades/escrita/preparacao/${compositionId}`);
      return;
    }

    checkNetworkStatus({
      offlineCallback: () => {
        if (
          (activity.status === 'reviewed' || hasFinishedLocalComposition) &&
          isMultiGenreReviewGrid
        ) {
          navigate({
            pathname: `/atividades/escrita/${compositionId}${baseReviewedRoute}/${
              activity.uses_ai_comp ? 'central' : 'apresentacao'
            }`,
            search: location.search,
          });
          return;
        }
      },
    });

    if (
      (activity.status === 'reviewed' || activity.status === 'finished') &&
      isMultiGenreReviewGrid
    ) {
      navigate(
        `/atividades/escrita/${compositionId}${baseReviewedRoute}/${
          activity.uses_ai_comp ? 'central' : 'apresentacao'
        }`,
      );
      return;
    }

    if (activity.status === 'reviewed') {
      const route =
        activity.uses_ai_comp || isFuvestReviewGrid
          ? `/atividades/escrita/${compositionId}/corrigida`
          : `/atividades/escrita/${compositionId}/nova_devolutiva/apresentacao`;

      navigate({pathname: route, search: location.search});
      return;
    }

    if (activity.status === 'expired') {
      navigate('/');
      return;
    }
    navigate(`/atividades/escrita/${compositionId}`);
  }

  function handleCardClick() {
    // if has a reminder child opens the feedback modal
    if (children?.props?.['data-id']?.includes('reminder')) {
      setActiveModal('feedback');
      return;
    }
    redirectToActivity();
  }

  function redirectToActivity(skipFocusModeInstructions = false) {
    closeTour?.();

    if (isLearningPath) {
      if (shouldShowFocusModeInstructionModal && !skipFocusModeInstructions) {
        modals[focusModeModalId]?.showModal();
        return;
      }

      const shouldSkipOptimisticStartDate =
        activity?.uses_focus_mode && !activity?.started;

      if (!shouldSkipOptimisticStartDate) {
        cachedActivity.updateStartDate(activity.id).catch(() => {});
      }

      // For focus mode activities, respect util's enabled decision before navigating.
      if (activity?.uses_focus_mode) {
        const focusModeState = getFocusModeCardState(activity);
        const effectiveEnabled =
          focusModeState.enabled || Boolean(hasFinishedLocalComposition);
        if (!effectiveEnabled) {
          navigate('/');
          return;
        }
      }

      if (activity.status === 'finished') {
        navigateToLearningPath.lastReviewOrFirstStep();
        return;
      }
      navigateToLearningPath.currentStep();
      return;
    }

    if (isTest) {
      if (activity.status === 'opened') {
        checkNetworkStatus({
          onlineCallback: () => {
            navigate(`/atividades/escrita/${activity.id}/criar`);
          },
          offlineCallback: () => {
            // TODO: implement warning "impossible to start offline, please download it first"
          },
        });
      } else {
        redirectToCreatedTest();
      }
    }
  }

  async function handleStartFocusModeActivity(): Promise<void> {
    if (!activity.id) {
      setFocusModeStartError('Não foi possível iniciar o Modo Foco.');
      return;
    }

    setFocusModeStartError('');

    const startResponse = await startFocusModeLearningPath.trigger(
      {
        learningPathUserInstanceId: activity.id,
      },
      {
        onError: (error) => {
          setFocusModeStartError(
            error?.response?.data?.error ||
              'Não foi possível iniciar o Modo Foco.',
          );
        },
      },
    );

    if (!startResponse) {
      setFocusModeStartError('Não foi possível iniciar o Modo Foco.');
      return;
    }

    cachedActivity
      .updateFocusModeStart(
        activity.id,
        startResponse.started,
        startResponse.completion_deadline,
      )
      .catch(() => {});

    setFocusModeStartError('');
    redirectToActivity(true);
  }

  const customClassForBorder = activityType.replace(/ /g, '-').toLowerCase();

  return (
    <li
      className={`${styles.container} ${
        isUnavailableActivity ? styles.unavailableContainer : ''
      } ${styles[customClassForBorder]}`}
      data-testid={`card-${activity?.id}`}
    >
      <>
        <DateTimeSpan
          startDateTime={activity?.start_datetime ?? ''}
          endDateTime={activity?.end_datetime ?? ''}
          status={activity?.status ?? ''}
        />

        <div className={styles.contentText}>
          {tags}
          <h2 className={styles.title}>
            {activity?.contest_id && (
              <FontAwesomeIcon icon={['fad', 'trophy']} color="#7518f7" />
            )}{' '}
            {activityName}
          </h2>
          <p className={styles.subtitle}>{activitySubTitle}</p>

          <p className={styles.activityType}>
            Tipo de Atividade: {learningPathTypes[activityType]}
          </p>
        </div>

        {activity && <ActivityStepper activity={activity} />}

        <div
          className={styles.statusButton}
          data-focusid={isUnavailableActivity ? '' : 'startActivityButton'}
        >
          {(() => {
            if (activity?.uses_focus_mode) {
              const focusModeState = getFocusModeCardState(activity);
              const effectiveEnabled =
                focusModeState.enabled || Boolean(hasFinishedLocalComposition);
              return (
                <Button
                  variant="primary"
                  onClick={handleCardClick}
                  disabled={!effectiveEnabled || isUnavailableActivity}
                >
                  <FontAwesomeIcon icon={['fas', 'lock']} color="grey" />
                  {focusModeState.buttonText}
                </Button>
              );
            }

            return (
              <Button
                variant="primary"
                onClick={handleCardClick}
                disabled={isUnavailableActivity}
              >
                <FontAwesomeIcon icon={['fas', 'lock']} color="grey" />
                {ButtonTexts[activity?.status]}
              </Button>
            );
          })()}
        </div>

        <div className={styles.downloadButton}>
          {activity && !isEvaluativeActivity && (
            <DownloadButton activity={activity} />
          )}
        </div>

        {isConcludedActivity && (
          <div className={styles.reviewBadgeContainer}>
            <div
              className={
                wasSeenByStudent
                  ? styles.seenReviewNotification
                  : styles.notSeenReviewNotification
              }
            >
              <FontAwesomeIcon
                icon={wasSeenByStudent ? 'bell' : 'bell-exclamation'}
                size="1x"
                color={wasSeenByStudent ? '#d9d9d9' : '#962f36'}
              />
            </div>

            {!wasSeenByStudent && (
              <span className={styles.reviewBadgeText}>
                Você ainda não viu a devolutiva desta atividade!
              </span>
            )}
          </div>
        )}

        {children}
      </>

      <FeedbackModal
        isOpen={activeModal === 'feedback'}
        onClose={() => setActiveModal(null)}
        reviewId={activity.last_review_goal?.id}
        onContinue={() => redirectToActivity()}
        competenceId={activity.last_review_goal?.competence_id}
        compositionId={activity.composition_id}
      />

      <FocusModeInstructionModal
        id={focusModeModalId}
        onStart={handleStartFocusModeActivity}
        loading={startFocusModeLearningPath.isMutating}
        error={focusModeStartError}
      />
    </li>
  );
}

export default DashboardCard;
